<template>
  <section class="page">
    <h2 class="page__title">{{ $t('kps.orders.title') }}</h2>

    <p v-if="loading">{{ $t('kps.orders.loadingOrder') }}</p>
    <p v-else-if="error" style="color:#D81717">{{ $t('common.error') }}: {{ error }}</p>

    <div v-if="editing" class="order-edit">
      <h3 class="order__title">{{ $t('kps.orders.orderHeading') }}</h3>
      <textarea class="form-textarea title-textarea" v-model="form.subtitle" :placeholder="$t('kps.orders.subtitlePlaceholder')"></textarea>
      <textarea class="form-textarea large" v-model="form.content" :placeholder="$t('kps.orders.contentPlaceholder')"></textarea>
    </div>

    <article v-else class="order">
      <h3 class="order__title">{{ $t('kps.orders.orderHeading') }}</h3>
      <div class="order__subtitle-view">{{ viewSubtitle }}</div>
      <div class="order__content">{{ viewContent }}</div>
    </article>

    <div class="page__footer">
      <ActionButton v-if="!editing" :outlined="true" :borderColor="'#111827'" :text="$t('common.edit')" @click="startEdit" />
      <template v-else>
        <ActionButton :color="'#2A8A1B'" :disabled="saving" :text="$t('common.save')" @click="save" />
        <ActionButton :outlined="true" :borderColor="'#111827'" :disabled="saving" :text="$t('common.cancel')" @click="cancelEdit" />
      </template>
      <ViewNavButtons class="page__footer-nav" :prev-text="$t('common.back')" :next-text="$t('common.next')" :prev-to="{ path: '/kps/personnel' }" :next-to="{ path: '/kps/calendar' }" />
    </div>
  </section>
</template>

<script>
import ViewNavButtons from '@/components/navigation/ViewNavButtons.vue'
import ActionButton from '@/components/widgets/ActionButton.vue'
import { getBookOrderFromContext, updateOrder } from '@/api/orders'
import { http, getRefresh, setAccess } from '@/api/http'

export default {
  name: 'KpsOrdersView',
  components: { ViewNavButtons, ActionButton },
  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      order: null,
      editing: false,
      form: { content: '', subtitle: '' },
    }
  },
  computed: {
    viewContent() { return this.form.content || this.defaultContent() },
    viewSubtitle() { return this.form.subtitle || this.defaultSubtitle() },
  },
  mounted() { this.loadOrder() },
  methods: {
    defaultSubtitle() { return 'О заступлении на охрану Государственной границы' },
    defaultContent() {
      return [
        'В связи с выполнением плана охраны участка государственной границы:',
        '1. С 08 мая 2025 года заступить на охрану участка № 5.',
        '2. Назначить старшего наряда и определить состав группы.',
        '3. Обеспечить наряд необходимыми средствами связи и наблюдения.',
        '4. Провести инструктаж по действиям при обнаружении нарушений.',
        '5. Доклад о заступлении представить в установленном порядке.',
      ].join('\n\n')
    },
    async loadOrder() {
      this.loading = true; this.error = null
      try {
        const order = await getBookOrderFromContext()
        if (order && order.text) {
          this.order = { id: order.id, rawText: order.text }
          const lines = String(order.text).split(/\r?\n/).filter(l => l.trim().length)
          const subtitle = lines.length ? lines[0] : this.defaultSubtitle()
          const body = lines.slice(1).join('\n\n') || this.defaultContent()
          this.form.subtitle = subtitle
          this.form.content = body
        } else {
          this.order = null
          this.form.subtitle = this.defaultSubtitle()
          this.form.content = this.defaultContent()
        }
      } catch (e) {
        this.error = e?.message || String(e)
      } finally {
        this.loading = false
      }
    },
    startEdit() { this.editing = true },
    cancelEdit() {
      this.editing = false
      if (this.order && this.order.rawText) {
        const lines = String(this.order.rawText).split(/\r?\n/).filter(l => l.trim().length)
        this.form.subtitle = lines.length ? lines[0] : this.defaultSubtitle()
        this.form.content = lines.slice(1).join('\n\n') || this.defaultContent()
      } else {
        this.form.subtitle = this.defaultSubtitle()
        this.form.content = this.defaultContent()
      }
    },
    async save() {
      if (!this.order) { this.editing = false; return }
      this.saving = true; this.error = null
      try {
        const textCombined = [this.form.subtitle, this.form.content].filter(Boolean).join('\n')
        const updated = await updateOrder(this.order.id, { text: textCombined })
        this.order = { id: updated.id, rawText: updated.text }
        this.editing = false
      } catch (e) {
        // Fallback: при 401 пробуем рефреш и повтор
        const is401 = (e && (e.status === 401 || /unauthor/i.test(e?.message || '') || /unauthor/i.test(e?.payload?.detail || '')))
        if (is401) {
          try {
            const refresh = getRefresh()
            if (refresh) {
              const r = await http.post('users/token/refresh/', { refresh })
              const newAccess = r?.data?.access
              if (newAccess) setAccess(newAccess)
              // retry save
              const textCombined = [this.form.subtitle, this.form.content].filter(Boolean).join('\n')
              const updated = await updateOrder(this.order.id, { text: textCombined })
              this.order = { id: updated.id, rawText: updated.text }
              this.editing = false
              this.saving = false
              return
            }
          } catch (_) {
            // провал рефреша — пойдём в общий обработчик
          }
        }
        this.error = e?.message || String(e)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.page {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.page__title {
  margin: 0;
  color: #111827;
  font-size: 24px;
  font-weight: 700;
  line-height: 33.6px;
}

.order { display: grid; gap: 8px; }
.order__title { margin: 0; color: #000; font-size: 22px; font-weight: 700; line-height: 33px; }
.order__subtitle-view { white-space: pre-wrap; color: #000; font-size: 24px; font-weight: 700; line-height: 26px; margin-bottom: 12px; }
.order__content { white-space: pre-wrap; color: #000; font-size: 20px; line-height: 24px; }

.order-edit { display: grid; gap: 12px; }
.form-textarea { width: 100%; padding: 12px; border: 1px solid #E5E7EB; border-radius: 10px; font-size: 20px; line-height: 24px; resize: vertical; }
.form-textarea.title-textarea { font-weight: 700; min-height: 64px; }
.form-textarea.large { min-height: 320px; }

.page__footer { margin-top: 16px; display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.page__footer-nav { margin-left: auto; }
</style>
